import { lazy, Suspense } from "react";
import { useAsync } from "./useAsync.js";
import { neckDiagramCapabilities } from "./neckDiagrams/api.js";
import {
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import type { Me } from "./api.js";
import {
  actionCentreAccess,
  ActionCentreProvider,
  ActionCentreSidebarBadge,
  hasActionCentreAccess,
} from "./actionCentre.js";
import { SyncBadge } from "./SyncBadge.js";
import { Sidebar } from "./Sidebar.js";
import {
  canManageAutomations,
  canManageLessonBlocks,
  canManageSchoolDomain,
  isOwner,
  isStaff,
  useAuth,
} from "./auth.js";
import { NeckDiagramPlaybackProvider } from "./neckDiagrams/NeckDiagramViewing.js";
import { FamilyLayout } from "./family.js";
import { BugReporter, BugReporterProvider } from "./bugs/BugReporter.js";
import { Announcements } from "./inbox/Announcements.js";
import { Inbox } from "./inbox/Inbox.js";
import { InboxReplyDraftProvider } from "./inbox/drafts.js";
import { InboxSidebarBadge, InboxUnreadProvider } from "./inbox/unread.js";
import { TutorMessageAlertsPrompt } from "./push/TutorMessageAlertsPrompt.js";
import {
  NotificationBell,
  Notifications,
  NotificationsProvider,
} from "./notifications/Bell.js";
import { Account } from "./pages/Account.js";
import { AuthUnavailable } from "./pages/AuthUnavailable.js";
import { Dashboard } from "./pages/Dashboard.js";
import { Diary } from "./pages/Diary.js";
import { ConfirmEmail } from "./pages/ConfirmEmail.js";
import { ForgotPassword } from "./pages/ForgotPassword.js";
import { Register } from "./pages/Register.js";
import { ResetPassword } from "./pages/ResetPassword.js";
import { LessonNotes } from "./pages/LessonNotes.js";
import { LessonResourceOpen } from "./pages/LessonResourceOpen.js";
import { StaffNotFoundRecovery } from "./pages/NotFoundRecovery.js";
import { isLessonResourceDestination } from "./lessonResourceDestination.js";
import { SignIn } from "./pages/SignIn.js";
import { StaffAccessibility } from "./pages/StaffAccessibility.js";
import { Student } from "./pages/Student.js";
import { TeacherHandoverPage } from "./pages/TeacherHandoverPage.js";
import { Students } from "./pages/Students.js";
import { Tutor } from "./pages/Tutor.js";
import { staffQuickActions } from "./quickActions.js";
import { useStaffAccessibilityPreferences } from "./staffAccessibility.js";
import { hasTodayWorkspace, staffLandingPath, staffSidebarItems } from "./staffNavigation.js";
import {
  hasCustomStudentColours,
  studentAccessibilityStyle,
} from "./studentAccessibilityTheme.js";
import { SyncProvider, useSync } from "./sync.js";

const HelpRoutes = lazy(() => import("./help/Help.js").then(module => ({ default: module.HelpRoutes })));

const Concerts = lazy(() =>
  import("./pages/Concerts.js").then((module) => ({ default: module.Concerts })),
);
const TutorConcertProgramme = lazy(() =>
  import("./pages/TutorConcertProgramme.js").then((module) => ({ default: module.TutorConcertProgramme })),
);
const PublicConcertProgrammePage = lazy(() =>
  import("./pages/PublicConcertProgramme.js").then((module) => ({ default: module.PublicConcertProgrammePage })),
);

// Secondary destinations are route chunks rather than startup dependencies.
// Keep the daily teaching path (Today, Diary, Students, lesson notes) eager so
// the bundle reduction does not trade initial startup for a delay on the most
// common first click. PR #313 serves these hashed chunks outside auth/session.
const AcceptInvite = lazy(() =>
  import("./pages/AcceptInvite.js").then((module) => ({ default: module.AcceptInvite })),
);
const AcceptHubInvite = lazy(() =>
  import("./pages/AcceptHubInvite.js").then((module) => ({ default: module.AcceptHubInvite })),
);
const Availability = lazy(() =>
  import("./pages/Availability.js").then((module) => ({ default: module.Availability })),
);
const CalendarBookLesson = lazy(() =>
  import("./pages/CalendarBookLesson.js").then((module) => ({ default: module.CalendarBookLesson })),
);
const CalendarOptions = lazy(() =>
  import("./pages/CalendarOptions.js").then((module) => ({ default: module.CalendarOptions })),
);
const CancelFutureLessons = lazy(() =>
  import("./pages/CancelFutureLessons.js").then((module) => ({ default: module.CancelFutureLessons })),
);
const MoveFutureLessons = lazy(() =>
  import("./pages/MoveFutureLessons.js").then((module) => ({ default: module.MoveFutureLessons })),
);
const ActionCentre = lazy(() =>
  import("./pages/Operations.js").then((module) => ({ default: module.ActionCentre })),
);
const MyTeachingIncome = lazy(() =>
  import("./pages/MyTeachingIncome.js").then((module) => ({
    default: module.MyTeachingIncome,
  })),
);
const LogPayment = lazy(() => import("./pages/LogPayment.js").then((module) => ({ default: module.LogPayment })));
const PaymentHistory = lazy(() => import("./pages/PaymentHistory.js").then((module) => ({ default: module.PaymentHistory })));
const PaidInvoices = lazy(() => import("./pages/PaidInvoices.js").then((module) => ({ default: module.PaidInvoices })));
const StaffDrumAssessments = lazy(() => import("./drums/DrumAssessments.js").then(m => ({ default: m.StaffDrumAssessments })));
const StaffGroupActivityAttendance = lazy(() =>
  import("./pages/GroupActivityAttendance.js").then((module) => ({ default: module.GroupActivityAttendance })),
);
const GroupActivityManagement = lazy(() =>
  import("./pages/GroupActivityManagement.js").then((module) => ({ default: module.GroupActivityManagement })),
);
const PracticeMetrics = lazy(() => import("./pages/PracticeMetrics.js").then(module=>({default:module.PracticeMetrics})));
const TutorPractice = lazy(() => import("./pages/TutorPractice.js").then(module=>({default:module.TutorPractice})));
const PitchPractice = lazy(() => import("./pages/PitchPractice.js").then(module=>({default:module.PitchPractice})));

const Payments = lazy(() =>
  import("./pages/Payments.js").then((module) => ({ default: module.Payments })),
);
const RecurringLessons = lazy(() =>
  import("./pages/RecurringLessons.js").then((module) => ({ default: module.RecurringLessons })),
);
const ReportsHome = lazy(() =>
  import("./pages/ReportsHome.js").then((module) => ({ default: module.ReportsHome })),
);
const EnquiryPipeline = lazy(() =>
  import("./pages/EnquiryPipeline.js").then((module) => ({ default: module.EnquiryPipeline })),
);
const LessonEngagementReport = lazy(() =>
  import("./pages/reports/LessonEngagementReport.js").then((module) => ({ default: module.LessonEngagementReport })),
);
const TimeOff = lazy(() =>
  import("./pages/TimeOff.js").then((module) => ({ default: module.TimeOff })),
);
const UnpaidLessons = lazy(() =>
  import("./pages/UnpaidLessons.js").then((module) => ({ default: module.UnpaidLessons })),
);
const SchoolPupil = lazy(() =>
  import("./pages/SchoolPupil.js").then((module) => ({ default: module.SchoolPupil })),
);
const SchoolDay = lazy(() =>
  import("./pages/SchoolDay.js").then((module) => ({ default: module.SchoolDay })),
);
const SchoolLessonForTutor = lazy(() =>
  import("./pages/SchoolLessonForTutor.js").then((module) => ({
    default: module.SchoolLessonForTutor,
  })),
);
const PublicGroove = lazy(() =>
  import("./pages/PublicGroove.js").then((module) => ({ default: module.PublicGroove })),
);
const SchoolGrooves = lazy(() =>
  import("./pages/SchoolGrooves.js").then((module) => ({ default: module.SchoolGrooves })),
);
const Files = lazy(() => import("./pages/Files.js").then(module => ({ default: module.Files })));
const RepertoireLibrary = lazy(() =>
  import("./pages/RepertoireLibrary.js").then((module) => ({ default: module.RepertoireLibrary })),
);
const Grooves = lazy(() =>
  import("./pages/Grooves.js").then((module) => ({ default: module.Grooves })),
);
const NeckDiagrams = lazy(() => import("./pages/NeckDiagrams.js").then(m => ({ default: m.NeckDiagrams })));
const NeckDiagram = lazy(() => import("./pages/NeckDiagram.js").then(m => ({ default: m.NeckDiagram })));
const SharedNeckDiagram = lazy(() => import("./pages/NeckDiagram.js").then(m => ({ default: m.SharedNeckDiagram })));
const GrooveEditor = lazy(() =>
  import("./pages/GrooveEditor.js").then((module) => ({ default: module.GrooveEditor })),
);
const SchoolPayers = lazy(() =>
  import("./pages/SchoolPayers.js").then((module) => ({ default: module.SchoolPayers })),
);
const SchoolInvoices = lazy(() =>
  import("./pages/SchoolInvoices.js").then((module) => ({ default: module.SchoolInvoices })),
);
const SchoolCredits = lazy(() =>
  import("./pages/SchoolCredits.js").then((module) => ({ default: module.SchoolCredits })),
);
const Invoicing = lazy(() =>
  import("./pages/Invoicing.js").then((module) => ({ default: module.Invoicing })),
);
const Staff = lazy(() =>
  import("./pages/Staff.js").then((module) => ({ default: module.Staff })),
);
const TeachingAssignmentQueue = lazy(() =>
  import("./pages/TeachingAssignments.js").then((module) => ({
    default: module.TeachingAssignmentQueue,
  })),
);
const LessonBlockSettings = lazy(() =>
  import("./pages/BookingSettings.js").then((module) => ({ default: module.LessonBlockSettings })),
);
const AutomationSettings = lazy(() =>
  import("./pages/BookingSettings.js").then((module) => ({ default: module.AutomationSettings })),
);
const AutomationDetail = lazy(() =>
  import("./pages/BookingSettings.js").then((module) => ({ default: module.AutomationDetail })),
);

/** How to name the signed-in account: an address, or a username where there is none. */
function signedInAs(me: Me): string {
  return me.user.email ?? me.user.username ?? "this account";
}

/**
 * Keep the permanent footer quiet: freshness is visible, account/recovery
 * actions are one deliberate click away. Normal syncing is server-scheduled;
 * these buttons are escape hatches rather than everyday navigation.
 */
function StaffSidebarFooter({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  const { status, busy, refreshBusy, syncNow, refreshMyDiary } = useSync();
  const owner = isOwner(me);
  const syncing = busy || status?.running === true;

  return (
    <div className="staff-sidebar-footer">
      <SyncBadge />
      <details className="sidebar-account-menu">
        <summary>
          <span className="sidebar-account-identity">
            <strong>Account</strong>
            <span>{signedInAs(me)}</span>
          </span>
          <span className="sidebar-account-more" aria-hidden="true">•••</span>
        </summary>
        <div className="sidebar-account-popover">
          <NavLink to="/account">Account</NavLink>
          {me.booking.staff && <NavLink to="/my-teaching-income">My teaching income</NavLink>}
          <NavLink to="/settings/accessibility">Accessibility</NavLink>
          {owner ? (
            <button type="button" onClick={() => void syncNow()} disabled={syncing}>
              {syncing ? "Syncing…" : "Sync all now"}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void refreshMyDiary()}
              disabled={refreshBusy || status?.running === true}
            >
              {refreshBusy ? "Refreshing…" : "Refresh my diary"}
            </button>
          )}
          <button className="sidebar-sign-out" type="button" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </details>
    </div>
  );
}

function LegacyMyStudentRedirect() {
  const { id = "" } = useParams<{ id: string }>();
  return (
    <Navigate
      to={`/students/${encodeURIComponent(id)}?scope=mine`}
      replace
    />
  );
}

function RouteLoading() {
  return <p className="empty" aria-live="polite">Loading…</p>;
}

const CaldayRoom = lazy(() => import("./calday-room/CaldayRoom.js"));

export function App() {
  const { me, loading, bootstrapError, retryBootstrap, signOut } = useAuth();
  const { pathname, search } = useLocation();
  const staffAppearance = useStaffAccessibilityPreferences(me?.user.id ?? null);
  const neckCapabilities = useAsync(() => me && isStaff(me) ? neckDiagramCapabilities() : Promise.resolve({ canCreate: false, instrument: null, canPlay: false, canUseLibrary: false }), [me?.user.id, JSON.stringify(me?.roles)]);
  const canManageSchool = canManageSchoolDomain(me);
  const manageLessonBlocks = canManageLessonBlocks(me);
  const manageAutomations = canManageAutomations(me);

  // Before the sign-in gate, deliberately. This is the address in a published
  // lesson note, and the person opening it is a parent on a phone or a School
  // pupil with no account — the token is the whole of the authority, as it is
  // for the engraved image in the same email.
  if (pathname.startsWith("/g/")) {
    return (
      <Suspense fallback={<RouteLoading />}>
        <PublicGroove />
      </Suspense>
    );
  }

  if (pathname === "/forgot-password") return <ForgotPassword />;
  if (pathname === "/reset-password") return <ResetPassword />;
  if (pathname === "/register") return <Register />;
  if (pathname === "/confirm-email") return <ConfirmEmail />;
  if (pathname === "/accept-invite") {
    return (
      <Suspense fallback={<RouteLoading />}>
        <AcceptInvite />
      </Suspense>
    );
  }
  if (pathname === "/hub-invite") {
    return (
      <Suspense fallback={<RouteLoading />}>
        <AcceptHubInvite />
      </Suspense>
    );
  }

  if (/^\/programme\/[^/]+$/.test(pathname)) {
    return (
      <Suspense fallback={<RouteLoading />}>
        <Routes><Route path="/programme/:token" element={<PublicConcertProgrammePage />} /></Routes>
      </Suspense>
    );
  }

  if (loading) return null;
  if (bootstrapError) {
    return <AuthUnavailable message={bootstrapError} onRetry={() => void retryBootstrap()} />;
  }
  if (!me && (pathname === "/help" || pathname.startsWith("/help/")) && new URLSearchParams(search).get("signin") !== "1") {
    return <Suspense fallback={<RouteLoading />}><Routes><Route path="/help/*" element={<HelpRoutes />} /></Routes></Suspense>;
  }
  if (!me) return <SignIn />;

  if (pathname === "/calday-room/manage") {
    return <Suspense fallback={<RouteLoading />}><CaldayRoom/></Suspense>;
  }

  if (isLessonResourceDestination(pathname)) return <LessonResourceOpen />;

  if (!isStaff(me)) {
    return <FamilyLayout onSignOut={() => void signOut()} />;
  }

  const staffHome = staffLandingPath(me);
  const canAccessToday = hasTodayWorkspace(me);
  const showActionCentre = hasActionCentreAccess(actionCentreAccess(me));
  const sidebarItems = staffSidebarItems(me, neckCapabilities.data?.canCreate === true)
    .filter((item) => item.label !== "Action Centre" || showActionCentre)
    .map((item) => {
      if (item.label === "Action Centre") {
        return {
          ...item,
          to: "/waiting",
          activeFor: ["/queue"],
          badge: <ActionCentreSidebarBadge />,
        };
      }
      // The Inbox count is a message count, not work outstanding. It is
      // deliberately a different badge from the Action Centre's for that
      // reason: one says somebody wrote to you, the other says something needs
      // deciding, and a single number over both would answer neither.
      return item.label === "Inbox" ? { ...item, badge: <InboxSidebarBadge /> } : item;
    });
  const quickActions = staffQuickActions(me);

  const customColours = hasCustomStudentColours(staffAppearance.preferences);
  const customFontSize = staffAppearance.preferences.fontSize !== "default";
  const staffWorkspaceClass =
    ((pathname === "/diary" || pathname === "/repertoire" || /^\/bookings\/[^/]+\/lesson$/.test(pathname) || (pathname === "/schools" && new URLSearchParams(search).get("writeup") === "1"))
      ? "staff-workspace staff-workspace--wide" : "staff-workspace") +
    (pathname === "/diary" ? " staff-workspace--diary" : "");

  return (
    <BugReporterProvider key={me.user.id}>
    <NeckDiagramPlaybackProvider key={me.user.id} enabled={!neckCapabilities.loading && neckCapabilities.data?.canPlay === true}>
    <SyncProvider>
      <ActionCentreProvider me={me}>
        <InboxUnreadProvider>
        <NotificationsProvider>
        <InboxReplyDraftProvider key={me.user.id}>
        <div
          className={`shell staff-accessibility-shell${
            customColours ? " has-accessibility-colours" : ""
          }${customFontSize ? " has-accessibility-font-size" : ""}`}
          data-accessibility-spacing={staffAppearance.preferences.textSpacing}
          style={studentAccessibilityStyle(staffAppearance.preferences)}
        >
          <Sidebar
            items={sidebarItems}
            quickActions={quickActions}
            headerAction={<><BugReporter key={me.user.id} /><NotificationBell /></>}
            footer={<StaffSidebarFooter me={me} onSignOut={() => void signOut()} />}
          />

          <main className="main" id="main-content" tabIndex={-1}>
            <div className={staffWorkspaceClass}>
              <TutorMessageAlertsPrompt />
              <Suspense fallback={<RouteLoading />}>
                <Routes>
                  <Route path="/help/*" element={<HelpRoutes />} />
                  <Route path="/" element={<Navigate to={staffHome} replace />} />
                  <Route
                    path="/today"
                    element={canAccessToday ? <Dashboard /> : <Navigate to={staffHome} replace />}
                  />
                  <Route
                    path="/dashboard"
                    element={<Navigate to={staffHome} replace />}
                  />
                  <Route path="/diary" element={<Diary />} />
                  <Route path="/notifications" element={<Notifications />} />
                  <Route path="/inbox" element={<Inbox />} />
                  {/* Before the thread route, so "announcements" is never read
                      as a conversation id. */}
                  {isOwner(me) && (
                    <Route path="/inbox/announcements" element={<Announcements />} />
                  )}
                  <Route path="/inbox/:threadId" element={<Inbox />} />
                  {me.booking.staff && (
                    <Route path="/calendar/book" element={<CalendarBookLesson />} />
                  )}
                  <Route path="/availability" element={<Availability />} />
                  <Route path="/time-off" element={<TimeOff />} />
                  <Route path="/students" element={<Students />} />
                  {canAccessToday && <Route path="/practice-history" element={<PracticeMetrics/>}/>}
                  {canAccessToday && <Route path="/my-practice" element={<TutorPractice/>}/>}
                  {canAccessToday && <Route path="/pitch-practice" element={<PitchPractice/>}/>}
                  {canAccessToday && <Route path="/practice-leaderboard" element={<PracticeMetrics leaderboard/>}/>}
                  {canAccessToday && <Route path="/repertoire" element={<RepertoireLibrary />} />}
                  {canAccessToday && <Route path="/files" element={<Files />} />}
                  <Route path="/neck-diagrams" element={<NeckDiagrams />} />
                  <Route path="/neck-diagrams/shared/:shareId" element={<SharedNeckDiagram />} />
                  <Route path="/neck-diagrams/:id" element={<NeckDiagram />} />
                  {canAccessToday && <Route path="/drum-assessments" element={<StaffDrumAssessments/>}/>}
                  {canAccessToday && me.groupActivityAttendanceEnabled === true && (
                    <Route path="/group-activities" element={<StaffGroupActivityAttendance owner={isOwner(me)} />} />
                  )}
                  {isOwner(me) && me.groupActivityAttendanceEnabled === true && (
                    <Route path="/group-activity-management" element={<GroupActivityManagement />} />
                  )}
                  <Route path="/grooves" element={<Grooves />} />
                  <Route path="/grooves/:id" element={<GrooveEditor />} />
                  <Route path="/students/:id" element={<Student />} />
                  <Route path="/teacher-handovers/:id" element={<TeacherHandoverPage />} />
                  <Route
                    path="/my-students"
                    element={<Navigate to="/students?scope=mine" replace />}
                  />
                  <Route
                    path="/my-students/:id"
                    element={<LegacyMyStudentRedirect />}
                  />
                  {me.booking.staff && (
                    <Route
                      path="/students/:id/cancel-future"
                      element={<CancelFutureLessons />}
                    />
                  )}
                  {me.booking.staff && (
                    <Route
                      path="/students/:id/move-future"
                      element={<MoveFutureLessons />}
                    />
                  )}
                  {me.booking.staff && (
                    <Route path="/recurring-lessons" element={<RecurringLessons />} />
                  )}
                  {showActionCentre && <Route path="/waiting" element={<ActionCentre />} />}
                  {/* Keep the bare payments bookmark available to staff when
                      the booking integration is disconnected. Payments renders
                      its existing explanation in that state; action routes
                      remain gated below. */}
                  <Route path="/payments" element={<Payments />} />
                  {me.booking.staff && <Route path="/payments/log" element={<LogPayment />} />}
                  {me.booking.staff && <Route path="/payments/history" element={<PaymentHistory />} />}
                  {me.paymentInvoices && <Route path="/payments/invoices" element={<PaidInvoices />} />}
                  {me.booking.staff && <Route path="/my-earnings" element={<Navigate to="/my-teaching-income#customer-payments" replace />} />}
                  {me.booking.staff && (
                    <Route path="/my-teaching-income" element={<MyTeachingIncome />} />
                  )}
                  <Route path="/my-business-payments" element={<Navigate to="/my-teaching-income#business-payment-history" replace />} />
                  {me.booking.staff && (
                    <Route
                      path="/reports/unpaid-lessons"
                      element={
                        <div className="payments-detail-workspace">
                          <UnpaidLessons />
                        </div>
                      }
                    />
                  )}
                  {me.booking.staff && <Route path="/reports" element={<ReportsHome />} />}
                  {isOwner(me) && me?.enquiryPipelineEnabled === true && <Route path="/enquiries" element={<EnquiryPipeline />} />}
                  {isOwner(me) && <Route path="/reports/note-engagement" element={<LessonEngagementReport />} />}
                  {isOwner(me) && (
                    <Route
                      path="/teaching-assignments"
                      element={<TeachingAssignmentQueue />}
                    />
                  )}
                  <Route path="/tutors/:id" element={<Tutor />} />
                  <Route path="/bookings/:bookingId/lesson" element={<LessonNotes />} />
                  {showActionCentre && (
                    <Route
                      path="/queue"
                      element={<Navigate to="/waiting?queue=unmatched" replace />}
                    />
                  )}
                  {canManageSchool && <Route path="/schools" element={<SchoolDay />} />}
                  {/*
                    A tutor's own School lesson write-up, at the address the
                    diary links to. The server lets them into lessons they
                    teach and no others.
                  */}
                  {!canManageSchool && me?.roles.includes("tutor") && (
                    <Route path="/schools" element={<SchoolLessonForTutor />} />
                  )}
                  {canManageSchool && (
                    <Route path="/school-pupils/:id" element={<SchoolPupil />} />
                  )}
                  {canManageSchool && (
                    <Route path="/school-grooves" element={<SchoolGrooves />} />
                  )}
                  {canManageSchool && (
                    <Route path="/school-payers" element={<SchoolPayers />} />
                  )}
                  {canManageSchool && (
                    <Route path="/school-invoices" element={<SchoolInvoices />} />
                  )}
                  {canManageSchool && (
                    <Route path="/school-credits" element={<SchoolCredits />} />
                  )}
                  {/*
                    What the school pays its tutors. Owner-only here and gated
                    again on the server, which is the lock that matters.
                  */}
                  {isOwner(me) && me.concertsEnabled && <Route path="/concerts" element={<Concerts />} />}
                  {me.concertsEnabled && me.roles.includes("tutor") && (
                    <Route path="/concerts/:eventId/propose" element={<TutorConcertProgramme />} />
                  )}
                  {isOwner(me) && <Route path="/invoicing" element={<Invoicing />} />}
                  {isOwner(me) && <Route path="/staff" element={<Staff />} />}
                  {isOwner(me) && (
                    <Route
                      path="/staff/calendar-options"
                      element={<CalendarOptions />}
                    />
                  )}
                  <Route path="/settings" element={<Navigate to="/settings/accessibility" replace />} />
                  <Route
                    path="/settings/accessibility"
                    element={
                      <StaffAccessibility
                        preferences={staffAppearance.preferences}
                        onSave={staffAppearance.save}
                        onReset={staffAppearance.reset}
                        onApplied={staffAppearance.apply}
                      />
                    }
                  />
                  {manageLessonBlocks && (
                    <Route
                      path="/settings/lesson-blocks"
                      element={<LessonBlockSettings />}
                    />
                  )}
                  {manageAutomations && (
                    <Route
                      path="/settings/automations"
                      element={<AutomationSettings />}
                    />
                  )}
                  {manageAutomations && (
                    <Route
                      path="/settings/automations/:jobId"
                      element={<AutomationDetail />}
                    />
                  )}
                  <Route path="/account" element={<Account />} />
                  <Route
                    path="*"
                    element={<StaffNotFoundRecovery destination={staffHome} />}
                  />
                </Routes>
              </Suspense>
            </div>
          </main>
        </div>
        </InboxReplyDraftProvider>
        </NotificationsProvider>
        </InboxUnreadProvider>
      </ActionCentreProvider>
    </SyncProvider>
    </NeckDiagramPlaybackProvider>
    </BugReporterProvider>
  );
}

